import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Calories from 'components/desktop/BaseIngredient/Search/Components/SearchResultListRow/Calories';
import Density from 'components/desktop/BaseIngredient/Search/Components/SearchResultListRow/Density';
import Picture from 'components/desktop/BaseIngredient/Search/Components/SearchResultListRow/Picture';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import FormatedDate from 'components/desktop/_general/FormatedDate/FormatedDate';
import IconComponent from 'components/desktop/_general/Icon/IconComponent';
import IconCo2Round from 'components/desktop/_general/IconCo2/IconCo2Round';
import NutriScoreDiagram from 'components/desktop/_general/NutriScoreDiagram/NutriScoreDiagram';
import SeasonalityBar from 'components/desktop/_general/SeasonalityBar/SeasonalityBar';
import getSeasonalityCode from 'functions/getSeasonalityCode';
import usePreferredAllergens from 'hooks/redux/usePreferredAllergens';
import * as BaseIngredient from 'types/BaseIngredient/Search/BaseIngredient';
import * as Icon from 'types/Icon/Icon';

interface IProps {
	baseIngredient: BaseIngredient.Type;
	handleRestoreBaseIngredient: (input: string) => Promise<void>;
}

const ArchiveResultListRow = (props: IProps) => {
	const { t } = useTranslation();
	const preferredAllergens = usePreferredAllergens();

	const allergensUserSet = useMemo(
		() => preferredAllergens.toSet((x) => `${x.id}_${x.logic}`),
		[preferredAllergens]
	);

	const getButtonDataRestore = (): BtnData => {
		return {
			onClick: () => props.handleRestoreBaseIngredient(props.baseIngredient.id),
		};
	};

	const renderButtons = (): JSX.Element => {
		return <BtnGroup restore={getButtonDataRestore()} />;
	};

	return (
		<div className="recipe-table-item" key={props.baseIngredient.name}>
			<Picture image={props.baseIngredient.image} />
			<div id="column-first">
				<div id="title">
					<strong>{props.baseIngredient.name}</strong>
				</div>
				<div id="info">
					<Calories
						calories={props.baseIngredient.calories}
						characteristic={props.baseIngredient.characteristic}
					/>
					<Density density={props.baseIngredient.density} />
				</div>
			</div>
			<div id="column-second">
				<div className="icon-column justify-content-start">
					<SeasonalityBar
						seasonalityCode={getSeasonalityCode(props.baseIngredient.seasons)}
					/>
				</div>
				<div className="row">
					<div className="col-5">
						<span className="grey">{t('_general:LAST_CHANGE_DATE')}:</span>
						<br />
						<span className="grey">{t('_general:LAST_EDITOR')}:</span>
						<br />
					</div>
					<div className="col-7">
						<FormatedDate date={props.baseIngredient.lastEditedDateUtc} />
						<br />
						<span>{BaseIngredient.getAuthor(props.baseIngredient)}</span>
					</div>
				</div>
			</div>
			<div id="column-last">
				{props.baseIngredient.hasNutriScore && props.baseIngredient.nutriScore && (
					<div className="justify-content-end">
						<NutriScoreDiagram nutriScore={props.baseIngredient.nutriScore} />
					</div>
				)}
				<div id="icon-rows">
					{BaseIngredient.getIcons(props.baseIngredient, allergensUserSet)
						.slice(0, 4)
						.map((icon: Icon.Type, i: number) => (
							<IconComponent icon={icon} key={i} />
						))}
					<IconCo2Round
						color={
							props.baseIngredient.carbonDioxideLabel
								? props.baseIngredient.carbonDioxideLabel.color!
								: ''
						}
						label={
							props.baseIngredient.carbonDioxideLabel
								? props.baseIngredient.carbonDioxideLabel.label!
								: ''
						}
					/>
				</div>
				<div>{renderButtons()}</div>
			</div>
		</div>
	);
};

export default ArchiveResultListRow;
